import {
  Box,
  Button,
  Card,
  DataDisplay,
  Grid,
  GridBreak,
  InfoTooltip,
  RichTextReader,
  Stack,
  Typography,
  deepCopy,
} from "@compass/core";
import { FilePanel, Select, Input } from "components";
import {
  Application,
  ApplicationQuestion,
  ApplicationResponse,
  ApplicationSection,
  PublicApplicationAnswerDto,
  PublicApplicationQuestionDto,
  PublicApplicationResponseDto,
  PublicApplicationSubSectionDto,
  PublicApplicationViewResource,
} from "generated/openApi";
import { useDateTime, useRouter } from "hooks";
import { useMediaQuery } from "@mui/material";
import { ApplicationPropertyEnum } from "pages/cobro/applications/ApplicationPage/Questions/ApplicationPropertyEnum";
import { FormQuestionTypes } from "pages/cobro/surveys/SurveyPage/Questions/SurveyQuestionEnum";
import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import { CheckboxGroup } from "../common/CheckboxGroup";
import { RadioGroup, RadioOption } from "../common/RadioGroup";
import { TransientFormAnswer, TransientQuestionResponse } from "../common/TransientQuestionResponse";
import { FinancialTablePage } from "./GUUtahFinancialTable";
import { homelessDefinitionPageRoute } from "./HomelessDefinition";
import { OpenEndedQuestion } from "../common/OpenEndedQuestion";
import { MatrixGroup, MatrixOption, MatrixQuestion } from "../common/MatrixGroup";
import { SignatureBox } from "../common/SignatureBox";
import { DropdownQuestion } from "../common/DropdownQuestion";

type publicSurveyApplicationPageProps = {
  application: Application;
  activeQuestions: ApplicationQuestion[];
  viewResource: PublicApplicationViewResource;
  transientQuestionResponses: TransientQuestionResponse[];
  validationErrors: object | undefined;
  isViewMode: boolean;
  isReviewMode: boolean;
  applicationResponse?: ApplicationResponse;

  updateQuestionResponse: (value: TransientQuestionResponse) => void;
  submitResponse: (applicationResponse: PublicApplicationResponseDto) => void;
};

type transientApplicationSubSection = {
  subSectionId: number;
  questions: ApplicationQuestion[];
};

type ApplicationGroupItem = {
  question: PublicApplicationQuestionDto;
  index: number;
};

type ApplicationGroup = {
  questions: ApplicationGroupItem[];
};

export const PublicApplicationQuestionPage = ({
  application,
  activeQuestions,
  viewResource,
  transientQuestionResponses,
  validationErrors,
  isViewMode,
  isReviewMode,
  applicationResponse,
  updateQuestionResponse,
  submitResponse,
}: publicSurveyApplicationPageProps) => {
  const navigate = useNavigate();
  const { matchTemplatePath } = useRouter();
  const [subsections, setSubsections] = useState<transientApplicationSubSection[]>([]);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [parentSignature, setParentSignature] = useState<any>({});
  const [studentSignature, setStudentSignature] = useState<any>({});
  const formatDateTime = useDateTime();
  const [currentPageIndex, setCurrentPageIndex] = useState<number>(Math.min(...application.applicationSections!.map((s) => s.pageIndex)) || 0);
  const [maxPageIndex, setMaxPageIndex] = useState<number>(
    Math.max(...application.applicationSections!.map((s) => s.pageIndex))
  );

  const [maxDisplayOrder, setMaxDisplayOrder] = useState<number | null>(null);
  const [endAnswerText, setEndAnswerText] = useState<string | null>(null);

  useEffect(() => {
    const temp: transientApplicationSubSection[] = [];
    viewResource.sections?.forEach((s) => { });
  }, [viewResource]);

  const scrollToErrorRef = useRef<null | number>(null);

  useEffect(() => {
    if (scrollToErrorRef.current !== null) {
      // If we have an error scroll target queued, scroll to that instead of top
      const element = document.getElementById(`questionId${scrollToErrorRef.current}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      scrollToErrorRef.current = null; // reset after scrolling
    } else {
      // Normal navigation -> scroll to top
      document.getElementById("topPage")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [currentPageIndex]);

  useEffect(() => {
    const erroredQuestions: number[] = [];

    for (const error in validationErrors) {
      const questionId = parseInt(error.replace("questionId", ""));
      erroredQuestions.push(questionId);
    }

    if (!erroredQuestions.length) return;

    let firstErroredQuestionId: number | null = null;

    application?.applicationSections?.forEach((section) => {
      section.applicationQuestions?.forEach((question) => {
        if (firstErroredQuestionId == null && erroredQuestions.includes(question.id)) {
          firstErroredQuestionId = question.id;

          // Mark this question as our scroll target
          scrollToErrorRef.current = question.id;

          // Trigger page change
          setCurrentPageIndex(section.pageIndex);
        }
      });
    });
  }, [validationErrors]);

  useEffect(() => {
    if (maxDisplayOrder) {
      // if we have an end question, set the max page count to +1 than it currently is
      setMaxPageIndex(currentPageIndex + 1);
    } else {
      setMaxPageIndex(Math.max(...application.applicationSections!.map((s) => s.pageIndex)));
    }
  }, [maxDisplayOrder]);

  const submitApplication = () => {
    const applicationResponse: PublicApplicationResponseDto = {
      applicationGuid: application.guid!,
      isReviewer: false, // will set this in PublicApplicationPage
      parentSignatureData: application.optionalSignatures
        ? ""
        : parentSignature.getTrimmedCanvas().toDataURL("image/png"),
      studentSignatureData: application.optionalSignatures
        ? ""
        : studentSignature.getTrimmedCanvas().toDataURL("image/png"),
      applicationQuestionResponses: [],
      endAnswerDisplayOrder: maxDisplayOrder || undefined
    };
    submitResponse(applicationResponse);
  };

  const navigateHomelessDefinition = () => {
    navigate({
      pathname: homelessDefinitionPageRoute.getPath({ languageId: application.languageId! }),
    });
  };

  const renderRadioAnswers = (question: PublicApplicationQuestionDto, transientResponse: TransientQuestionResponse) => {
    const options: RadioOption[] = [];
    question.applicationAnswers!.map((f) =>
      options.push({
        label: f.name!,
        valueType: f.applicationAnswerId == 0 ? "EntityId" : "AnswerId",
        value: f.applicationAnswerId == 0 ? f.applicationPropertyEntityId! : f.applicationAnswerId,
        isOther: f.isOther,
        isOtherValue: null,
        otherTypeId: f.otherWriteInTypeId,
        isEndAnswer: f.isEndAnswer,
        endAnswerText: f.endAnswerText,
      })
    );

    return (
      <RadioGroup
        isDisabled={isViewMode}
        value={transientResponse.formAnswers[0]?.formAnswerId || transientResponse.formAnswers[0]?.formEntityId || null}
        isOtherValue={transientResponse.formAnswers[0]?.writeIn}
        options={options}
        onChange={(option) => {
          if (option.valueType == "EntityId") {
            var temp: TransientFormAnswer = { formEntityId: option.value };
            updateQuestionResponse({ ...transientResponse, formAnswers: [temp] });
          } else {
            var temp: TransientFormAnswer = { formAnswerId: option.value };
            updateQuestionResponse({ ...transientResponse, formAnswers: [temp] });
          }

          if (question.applicationAnswers?.some((a) => a.isEndAnswer)) {
            if (option.isEndAnswer) {
              setMaxDisplayOrder(question.displayOrder);
              setEndAnswerText(option.endAnswerText);
            } else {
              setMaxDisplayOrder(null);
            }
          }
        }}
        onOtherChange={(formAnswerId, otherValue) => {
          var changedOption = options.find((o) => o.value == formAnswerId);
          changedOption!.isOtherValue = otherValue;
          var temp: TransientFormAnswer = { formAnswerId: formAnswerId, writeIn: otherValue };
          updateQuestionResponse({ ...transientResponse, formAnswers: [temp] });
        }}
      />
    );
  };

  const renderDropDown = (question: PublicApplicationQuestionDto, transientResponse: TransientQuestionResponse) => {
    const options: RadioOption[] = [];

    question.applicationAnswers!.map((f) =>
      options.push({
        label: f.name!,
        valueType: f.applicationAnswerId == 0 ? "EntityId" : "AnswerId",
        value: f.applicationAnswerId == 0 ? f.applicationPropertyEntityId! : f.applicationAnswerId,
        isOther: f.isOther,
        isOtherValue: f.isOther && transientResponse.formAnswers[0]?.writeIn ? transientResponse.formAnswers[0]?.writeIn : null,
        otherTypeId: f.otherWriteInTypeId,
        isEndAnswer: f.isEndAnswer,
        endAnswerText: f.endAnswerText,
      })
    );

    return (
      <DropdownQuestion
        value={transientResponse.formAnswers[0]?.formAnswerId || transientResponse.formAnswers[0]?.formEntityId || null}
        options={options}
        isDisabled={isViewMode}
        onChange={(option) => {
          if (option.valueType == "EntityId") {
            var temp: TransientFormAnswer = { formEntityId: option.value };
            updateQuestionResponse({ ...transientResponse, formAnswers: [temp] });
          } else {
            var temp: TransientFormAnswer = { formAnswerId: option.value };
            updateQuestionResponse({ ...transientResponse, formAnswers: [temp] });
          }

          if (question.applicationAnswers?.some((a) => a.isEndAnswer)) {
            if (option.isEndAnswer) {
              setMaxDisplayOrder(question.displayOrder);
              setEndAnswerText(option.endAnswerText);
            } else {
              setMaxDisplayOrder(null);
            }
          }
        }}
        onOtherChange={(formAnswerId, otherValue) => {
          var changedOption = options.find((o) => o.value == formAnswerId);
          changedOption!.isOtherValue = otherValue;
          var temp: TransientFormAnswer = { formAnswerId: formAnswerId, writeIn: otherValue };
          updateQuestionResponse({ ...transientResponse, formAnswers: [temp] });
        }}
      />
    );
  };

  const findQuestionByPropertyId = (applicationPropertyId: number): ApplicationQuestion | null => {
    let applicationQuestion: ApplicationQuestion | null = null;
    application.applicationSections?.forEach((section) => {
      var question = section.applicationQuestions?.find((q) => q.applicationPropertyId == applicationPropertyId);
      if (question) return (applicationQuestion = question);
    });

    return applicationQuestion;
  };

  // takes in a question, and compares to see if the answer equals a string
  const compareResponseAnswer = (question: ApplicationQuestion, comparisonStringArray: string[]): boolean => {
    let comparisonAnswer;

    for (const i of comparisonStringArray) {
      comparisonAnswer = question.applicationAnswers?.find((a) => stripHtml(a.name?.toLowerCase()) === i.toLowerCase());

      if (comparisonAnswer) {
        break; // exit the loop on the first match
      }
    }

    // i.e the question does not contain the string it's being compared to
    if (!comparisonAnswer) return false;

    // check to see if the response has this answer
    const questionResponse = findResponseByQuestionId(question.id);

    // i.e no response has been created for this question yet
    if (!questionResponse) return false;

    // if the response matches the answer id, we got a match!
    if (questionResponse.formAnswers[0]?.formAnswerId == comparisonAnswer.id) return true;

    // handle "yes" / "no" boolean values (i.e "Yes" has an formEntityId == 1 instead of using AnswerId)
    if (stripHtml(comparisonAnswer.name.toLowerCase()) == "yes" || stripHtml(comparisonAnswer.name.toLowerCase()) == "si") {
      if (questionResponse.formAnswers[0]?.formEntityId == 1) return true;
    }

    // else, no match was found
    return false;
  };

  const findResponseByQuestionId = (applicationQuestionId: number): TransientQuestionResponse | undefined => {
    let applicationQuestionResponse: TransientQuestionResponse | undefined = transientQuestionResponses.find(
      (r) => r.questionId == applicationQuestionId
    );
    return applicationQuestionResponse;
  };

  const canIncreasePageIndex = (): boolean => {
    // console.log("current", currentPageIndex);
    // console.log("maxPageIndex", maxPageIndex);

    if (currentPageIndex != maxPageIndex) return true;
    else return false;
  };

  const increasePageIndex = () => {
    setCurrentPageIndex(currentPageIndex + 1);
  };

  const decreasePageIndex = () => {
    setCurrentPageIndex(currentPageIndex - 1);
  };

  // for mapping one response to another, for instance when a parent's address is the same as the students, goes from source (student) => target (parent)
  const mapResponse = (sourceQuestion: ApplicationQuestion, targetQuestion: ApplicationQuestion) => {
    var sourceResponse = findResponseByQuestionId(sourceQuestion.id);

    // if source response doesn't exist, do nothing
    if (!sourceResponse) return;
    // set target question response to equal source, but with target question ID
    var updatedResponse = { ...sourceResponse, questionId: targetQuestion.id };

    // if the source has an answer, copy over to a matching target question answer if applicable
    if (sourceResponse.formAnswers.length > 0) {
      updatedResponse.formAnswers = [];
      const sourceAnswer = sourceQuestion.applicationAnswers?.find(
        (a) => a.id == sourceResponse!.formAnswers[0].formAnswerId
      );
      if (!sourceAnswer) return;
      const targetAnswer = targetQuestion.applicationAnswers?.find((a) => a.name == sourceAnswer.name);
      if (!targetAnswer) return;
      updatedResponse.formAnswers.push({ formAnswerId: targetAnswer?.id });
    }

    updateQuestionResponse(updatedResponse);
  };

  const sameAddressAsStudent = (question: ApplicationQuestion) => {
    if (question.applicationPropertyId == ApplicationPropertyEnum.Parent1AddressSameAsStudent) {
      // student questions
      const studentAddressQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.Address);
      const studentCityQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.City);
      const studentStateQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.State);
      const studentZipCodeQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.ZipCode);

      // parent questions
      const parent1AddressQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.Parent1Address);
      const parent1CityQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.Parent1City);
      const parent1StateQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.Parent1State);
      const parent1ZipCodeQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.Parent1ZipCode);

      // map values
      if (studentAddressQuestion && parent1AddressQuestion) {
        mapResponse(studentAddressQuestion, parent1AddressQuestion);
      }
      if (studentCityQuestion && parent1CityQuestion) {
        mapResponse(studentCityQuestion, parent1CityQuestion);
      }
      if (studentStateQuestion && parent1StateQuestion) {
        mapResponse(studentStateQuestion, parent1StateQuestion);
      }
      if (studentZipCodeQuestion && parent1ZipCodeQuestion) {
        mapResponse(studentZipCodeQuestion, parent1ZipCodeQuestion);
      }
    } else if (question.applicationPropertyId == ApplicationPropertyEnum.Parent2AddressSameAsStudent) {
      // student questions
      const studentAddressQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.Address);
      const studentCityQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.City);
      const studentStateQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.State);
      const studentZipCodeQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.ZipCode);

      // parent questions
      const parent2AddressQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.Parent2Address);
      const parent2CityQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.Parent2City);
      const parent2StateQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.Parent2State);
      const parent2ZipCodeQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.Parent2ZipCode);

      // map values
      if (studentAddressQuestion && parent2AddressQuestion) {
        mapResponse(studentAddressQuestion, parent2AddressQuestion);
      }
      if (studentCityQuestion && parent2CityQuestion) {
        mapResponse(studentCityQuestion, parent2CityQuestion);
      }
      if (studentStateQuestion && parent2StateQuestion) {
        mapResponse(studentStateQuestion, parent2StateQuestion);
      }
      if (studentZipCodeQuestion && parent2ZipCodeQuestion) {
        mapResponse(studentZipCodeQuestion, parent2ZipCodeQuestion);
      }
    }
  };

  const renderMultipleChoice = (
    question: PublicApplicationQuestionDto,
    transientResponse: TransientQuestionResponse
  ) => {
    const options: RadioOption[] = [];
    question.applicationAnswers!.map((f) =>
      options.push({
        label: f.name!,
        valueType: f.applicationAnswerId == 0 ? "EntityId" : "AnswerId",
        value: f.applicationAnswerId == 0 ? f.applicationPropertyEntityId! : f.applicationAnswerId,
        isOther: f.isOther,
        isOtherValue: null,
      })
    );

    return (
      <CheckboxGroup
        isDisabled={isViewMode}
        values={transientResponse.formAnswers.flatMap((x) => x.formAnswerId!)}
        options={options}
        onChange={(optionValue, isChecked) => {
          var temp = deepCopy(transientResponse.formAnswers);
          if (isChecked) {
            temp.push({ formAnswerId: optionValue });

            if (
              question.applicationQuestion!.applicationPropertyId ==
              ApplicationPropertyEnum.Parent1AddressSameAsStudent ||
              question.applicationQuestion!.applicationPropertyId == ApplicationPropertyEnum.Parent2AddressSameAsStudent
            ) {
              sameAddressAsStudent(question.applicationQuestion!);
            }
          } else {
            var removeIndex = temp.indexOf(optionValue);
            temp.splice(removeIndex, 1);
          }
          updateQuestionResponse({ ...transientResponse, formAnswers: temp });
        }}
        onOtherChange={(formAnswerId, otherValue) => {
          var temp = deepCopy(transientResponse.formAnswers);
          var changedOption = temp.find((o) => o.formAnswerId == formAnswerId);
          changedOption!.writeIn = otherValue;

          updateQuestionResponse({ ...transientResponse, formAnswers: temp });
        }}
      />
    );
  };

  const renderMatrix = (question: PublicApplicationQuestionDto, transientResponse: TransientQuestionResponse) => {
    const subQuestions: MatrixQuestion[] = [];
    const options: MatrixOption[] = [];
    question.applicationQuestion!.applicationSubQuestions!.map((sq) =>
      subQuestions.push({ label: sq.label, question: sq.name, subQuestionId: sq.id })
    );
    question.applicationAnswers!.map((fa) =>
      options.push({ label: fa.name || "", formAnswerId: fa.applicationAnswerId })
    );

    return (
      <MatrixGroup
        applicationQuestion={question!.applicationQuestion!}
        isDisabled={isViewMode}
        values={transientResponse.formAnswers}
        subQuestions={subQuestions}
        options={options}
        handleChange={(optionValue, isChecked) => {
          var temp = deepCopy(transientResponse.formAnswers);
          if (question.applicationQuestion!.formQuestionTypeId == FormQuestionTypes.MatrixMultipleChoice) {
            if (isChecked) {
              temp.push(optionValue);
            } else {
              var removeIndex = temp.findIndex((t) => t.formAnswerId == optionValue.formAnswerId);
              temp.splice(removeIndex, 1);
            }
          } else if (question.applicationQuestion!.formQuestionTypeId == FormQuestionTypes.MatrixWriteIn) {
            const tempIndex: number = temp.findIndex(
              (x) => x.subQuestionId == optionValue.subQuestionId && x.formAnswerId == optionValue.formAnswerId
            );
            if (tempIndex != -1) {
              temp[tempIndex] = optionValue;
            } else {
              temp.push(optionValue);
            }
          } else {
            const tempIndex: number = temp.findIndex((x) => x.subQuestionId == optionValue.subQuestionId);
            if (tempIndex != -1) {
              temp[tempIndex] = optionValue;
            } else {
              temp.push(optionValue);
            }
          }

          updateQuestionResponse({ ...transientResponse, formAnswers: temp });
        }}
      />
    );
  };

  const renderQuestionTop = (question: ApplicationQuestion, index: number) => {
    let questionResponseIndex: number = transientQuestionResponses.findIndex((qr) => qr.questionId == question.id)!;
    let transientResponse: TransientQuestionResponse = transientQuestionResponses![questionResponseIndex];

    let questionName = question.name;
    if ((question.isMandatory && !isReviewMode) || (isReviewMode && question.markedForReview)) {
      var index = question.name.indexOf("</p>");
      if (index !== -1) {
        var modifiedString =
          question.name.substring(0, index) + '<span style="color:red">*</span>' + question.name.substring(index);
        questionName = modifiedString;
      } else {
        var index = question.name.indexOf("</strong>");
        if (index !== -1) {
          var modifiedString =
            question.name.substring(0, index) + '<span style="color:red">*</span>' + question.name.substring(index);
          questionName = modifiedString;
        } else {
          var index = question.name.indexOf("</div>");
          if (index !== -1) {
            var modifiedString =
              question.name.substring(0, index) + '<span style="color:red">*</span>' + question.name.substring(index);
            questionName = modifiedString;
          }
        }
      }
    }

    return (
      <Grid
        item
        md={isMobile ? 12 : question.questionWidth == "dl-full" ? 12 : question.questionWidth == "dl-L" ? 6 : 4}
        xs={12}
        key={`questionTop:${index}`}
        id={`questionId${question.id}`}
      >
        <Grid container spacing="sm">
          {/* <Grid item xs={12} sx={{ maxHeight: question.questionWidth != "dl-full" ? "63.5px" : null }}> */}
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center">
              <RichTextReader
                html={questionName}
                customStyles={{
                  div: {
                    marginBottom: "1em",
                    marginTop: "1em",
                  },
                }}
              />
              {question.applicationPropertyId == ApplicationPropertyEnum.Homeless && (
                <InfoTooltip value="View definition of homeless" onClick={() => navigateHomelessDefinition()} />
              )}
            </Stack>
          </Grid>

          {validationErrors?.[`questionId${question.id}`] && (
            <Grid item xs={12}>
              <Typography color="error">{validationErrors?.[`questionId${question.id}`]}</Typography>
            </Grid>
          )}

          {question.instruction && (
            <Grid item xs={12}>
              <RichTextReader html={question.instruction} />
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderQuestionBottom = (question: PublicApplicationQuestionDto, index: number) => {
    let questionResponseIndex: number = transientQuestionResponses.findIndex(
      (qr) => qr.questionId == question.applicationQuestion!.id
    )!;
    let transientResponse: TransientQuestionResponse = transientQuestionResponses![questionResponseIndex];

    let questionName = question.applicationQuestion!.name;
    if (
      (question.applicationQuestion?.isMandatory && !isReviewMode) ||
      (isReviewMode && question.applicationQuestion?.markedForReview)
    ) {
      var index = question.applicationQuestion!.name.indexOf("</p>");
      if (index !== -1) {
        var modifiedString =
          question.applicationQuestion!.name.substring(0, index) +
          '<span style="color:red">*</span>' +
          question.applicationQuestion!.name.substring(index);
        questionName = modifiedString;
      } else {
        var index = question.applicationQuestion!.name.indexOf("</strong>");
        if (index !== -1) {
          var modifiedString =
            question.applicationQuestion!.name.substring(0, index) +
            '<span style="color:red">*</span>' +
            question.applicationQuestion!.name.substring(index);
          questionName = modifiedString;
        }
      }
    }

    return (
      <Grid
        item
        md={
          isMobile
            ? 12
            : question.applicationQuestion!.questionWidth == "dl-full"
              ? 12
              : question.applicationQuestion!.questionWidth == "dl-L"
                ? 6
                : 4
        }
        xs={12}
        key={`questionBottom:${index}`}
        id={`questionId${question.applicationQuestion!.id}`}
        sx={{ paddingTop: "0px !important" }}
      >
        <Grid container spacing="sm">
          <Grid item xs={12} sx={{ paddingTop: 0 }}>
            {question.applicationQuestion!.formQuestionTypeId == FormQuestionTypes.OpenEnded && (
              <OpenEndedQuestion
                isDisabled={isViewMode}
                question={question.applicationQuestion!}
                transientResponse={transientResponse}
                updateQuestionResponse={updateQuestionResponse}
              />
            )}
            {question.applicationQuestion!.formQuestionTypeId == FormQuestionTypes.MultipleChoiceDropDown && (
              <>{renderDropDown(question, transientResponse)}</>
            )}
            {question.applicationQuestion!.formQuestionTypeId == FormQuestionTypes.MultipleChoiceRadio && (
              <>{renderRadioAnswers(question, transientResponse)}</>
            )}
            {question.applicationQuestion!.formQuestionTypeId == FormQuestionTypes.MultipleChoiceCheckBox && (
              <>{renderMultipleChoice(question, transientResponse)}</>
            )}
            {(question.applicationQuestion!.formQuestionTypeId == FormQuestionTypes.Matrix ||
              question.applicationQuestion!.formQuestionTypeId == FormQuestionTypes.MatrixMultipleChoice ||
              question.applicationQuestion!.formQuestionTypeId == FormQuestionTypes.MatrixWriteIn ||
              question.applicationQuestion!.formQuestionTypeId == FormQuestionTypes.MatrixGrouped) && (
                <>{renderMatrix(question, transientResponse)}</>
              )}
            {question.applicationQuestion!.formQuestionTypeId == FormQuestionTypes.Signature && (
              <>{renderSignature(question, transientResponse)}</>
            )}
            {question.applicationQuestion!.formQuestionTypeId == FormQuestionTypes.SignatureParent && (
              <>{renderParentSignature(question, transientResponse)}</>
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderSubSection = (subSection: PublicApplicationSubSectionDto, index: number) => {
    // reset to new line every 12 (to account for grid)
    let rowSpace = 0;
    let grouping: ApplicationGroup[] = [];
    let currentGroup: ApplicationGroup = { questions: [] };

    subSection.questions
      ?.filter((q) => (maxDisplayOrder == null || q.displayOrder <= maxDisplayOrder) && activeQuestions.some(a => a.id == q.applicationQuestion?.id))
      .forEach((q, i) => {
        let questionSize = 12;
        if (!isMobile) {
          questionSize =
            q.applicationQuestion!.questionWidth == "dl-full"
              ? 12
              : q.applicationQuestion!.questionWidth == "dl-L"
                ? 6
                : 4;
        }

        if (rowSpace + questionSize > 12) {
          grouping.push(currentGroup);
          currentGroup = { questions: [] };
          rowSpace = 0;

          const t: ApplicationGroupItem = {
            question: q,
            index: i,
          };
          currentGroup.questions.push(t);
          rowSpace += questionSize;
        } else {
          const t: ApplicationGroupItem = {
            question: q,
            index: i,
          };
          currentGroup.questions.push(t);
          rowSpace += questionSize;
        }
      });

    grouping.push(currentGroup);

    return (
      <>
        {grouping.map((g, i) => (
          <Grid item xs={12} key={`subSection:${subSection.subSectionId}:${i}`}>
            <Grid container spacing="lg" alignItems="flex-start" justifyContent="flex-start" direction="row">
              {g.questions.map((q) => renderQuestionTop(q.question.applicationQuestion!, q.index))}
              <GridBreak />
              {g.questions.map((q) => renderQuestionBottom(q.question, q.index))}
            </Grid>
          </Grid>
        ))}
      </>
    );
  };

  const renderSection = (section: ApplicationSection, index: number) => {
    const subSections = viewResource.sections?.find((s) => s.section?.id == section.id)?.subSections;

    // don't show financial eligilibility if foster care, homeless, or title 1 school
    if (section.name == "Financial Eligibility" || section.name == "Elegibilidad Financiera") {
      const homelessQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.Homeless);
      const fosterCareQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.FosterCare);
      const schoolQuestion = findQuestionByPropertyId(ApplicationPropertyEnum.School);

      let isHomeless;
      let isFosterCare;
      let isTitle1;
      if (homelessQuestion) {
        isHomeless = compareResponseAnswer(homelessQuestion, ["yes", "si"]);
      }
      if (fosterCareQuestion) {
        isFosterCare = compareResponseAnswer(fosterCareQuestion, ["yes", "si"]);
      }
      if (schoolQuestion) {
        const schoolQuestionResponse = findResponseByQuestionId(schoolQuestion.id);
        if (schoolQuestionResponse?.formAnswers && schoolQuestionResponse?.formAnswers.length > 0) {
          const matchedSchool = viewResource.schools?.find(
            (s) => s.schoolId == schoolQuestionResponse?.formAnswers[0].formEntityId
          );
          if (matchedSchool) {
            isTitle1 = matchedSchool.isTitle1;
            if (isTitle1) return <></>;
          }
        }
      }
      if (isHomeless || isFosterCare) return <></>;
    }

    return (
      <Grid item xs={12} key={`section:${index}`}>
        <Grid container spacing="lg">
          <Grid item xs={12} pb={2}>
            <Card
              header={section.name}
              headerSx={{
                color: "white",
                backgroundColor: (theme) => application.themeColor || theme.palette.primary.dark,
                fontSize: "1.3rem",
                fontWeight: "bold",
              }}
              sx={{ maxWidth: "1000px", width: "100%" }}
            >
              <Grid container spacing="lg">
                <Grid item xs={12}>
                  <RichTextReader html={section.description} />
                </Grid>

                {subSections &&
                  subSections.length > 0 &&
                  (maxDisplayOrder == null ||
                    section.applicationQuestions?.some((q) => q.displayOrder <= maxDisplayOrder)) &&
                  subSections.map((ss, index) => renderSubSection(ss, index))}

                {/* If no subSections are defined, but questions are */}
                {subSections &&
                  subSections.length == 0 &&
                  section.applicationQuestions &&
                  section.applicationQuestions.length > 0 &&
                  section.applicationQuestions.map((q, index) => renderQuestionTop(q, index))}

                {(section.name == "Financial Eligibility" || section.name == "Elegibilidad Financiera") && (
                  <Grid item xs={12}>
                    <FinancialTablePage languageId={application.languageId!} />
                  </Grid>
                )}
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    );
  };


  const stripHtml = (html: string | undefined) => {
    if (html == undefined) return;
    return html.replace(/<[^>]*>/g, '');
  }

  const parentSignatureField = () => {
    return (
      <Grid item md={6} xs={12}>
        {!isViewMode && (
          <Stack direction="column">
            <Stack direction="row" py={2}>
              <Typography variant="h5">
                {application.languageId == 2 ? "Firma del padre/tutor legal" : "Parent Signature"}
              </Typography>
              {!application.optionalSignatures && <Typography color="error">*</Typography>}
            </Stack>
            {validationErrors?.[`parentSignature`] && (
              <Grid item xs={12}>
                <Typography color="error">{validationErrors?.[`parentSignature`]}</Typography>
              </Grid>
            )}
            <Grid container spacing="sm">
              <Grid item xs={12}>
                <Box sx={{ width: 350, height: 200, backgroundColor: "white" }}>
                  <SignatureCanvas
                    penColor="black"
                    ref={(ref) => {
                      setParentSignature(ref);
                    }}
                    canvasProps={{ width: 350, height: 200 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" onClick={() => parentSignature.clear()}>
                  {application.languageId == 2 ? "Borrar" : "Clear"}
                </Button>
              </Grid>
            </Grid>
          </Stack>
        )}
        {isViewMode && applicationResponse && (
          <Stack direction="column" spacing="sm">
            <FilePanel
              label="Parent Signature"
              fileName={applicationResponse.parentSignatureUploadFile?.fileName}
              fileRef={applicationResponse.parentSignatureUploadFile?.fileRef}
              fileSize={applicationResponse.parentSignatureUploadFile?.fileSize}
              uploadingData={applicationResponse.parentSignatureUploadFile?.uploadingData}
              imagePreviewData={applicationResponse.parentSignatureUploadFile?.imagePreviewData}
            />
            <Typography variant="caption">Signed: {formatDateTime(applicationResponse.parentSignatureUploadFile?.createdDate)}</Typography>
          </Stack>
        )}
      </Grid>
    );
  };

  const studentSignatureField = () => {
    return (
      <Grid item md={6} xs={12}>
        {!isViewMode && (
          <Stack direction="column">
            <Stack direction="row" py={2}>
              <Typography variant="h5">
                {application.languageId == 2 ? "Firma del estudiante" : "Student Signature"}
              </Typography>
              {!application.optionalSignatures && <Typography color="error">*</Typography>}
            </Stack>
            {validationErrors?.[`studentSignature`] && (
              <Grid item xs={12}>
                <Typography color="error">{validationErrors?.[`studentSignature`]}</Typography>
              </Grid>
            )}
            <Grid container spacing="sm">
              <Grid item xs={12}>
                <Box sx={{ width: 350, height: 200, backgroundColor: "white" }}>
                  <SignatureCanvas
                    penColor="black"
                    ref={(ref) => {
                      setStudentSignature(ref);
                    }}
                    canvasProps={{ width: 350, height: 200 }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" onClick={() => studentSignature.clear()}>
                  {application.languageId == 2 ? "Borrar" : "Clear"}
                </Button>
              </Grid>
            </Grid>
          </Stack>
        )}
        {((isViewMode && applicationResponse) || (isReviewMode && applicationResponse)) && (
          <Stack direction="column" spacing="sm">
            <FilePanel
              label="Student Signature"
              fileName={applicationResponse.studentSignatureUploadFile?.fileName}
              fileRef={applicationResponse.studentSignatureUploadFile?.fileRef}
              fileSize={applicationResponse.studentSignatureUploadFile?.fileSize}
              uploadingData={applicationResponse.studentSignatureUploadFile?.uploadingData}
              imagePreviewData={applicationResponse.studentSignatureUploadFile?.imagePreviewData}
            />
            <Typography variant="caption">Signed: {formatDateTime(applicationResponse.studentSignatureUploadFile?.createdDate)}</Typography>
          </Stack>
        )}
      </Grid>
    );
  };

  const endAnswerTextField = () => {
    return (
      <Grid item xs={12} pb={2}>
        <Card
          header={" "}
          headerSx={{
            color: "white",
            backgroundColor: (theme) => application.themeColor || theme.palette.primary.dark,
            fontSize: "1.3rem",
            fontWeight: "bold",
          }}
          sx={{ maxWidth: "1100px", minWidth: "440px", width: "100%" }}
        >
          <Stack direction="column">
            <RichTextReader html={endAnswerText!} />
          </Stack>
        </Card>
      </Grid>
    );
  };

  const renderSignature = (question: PublicApplicationQuestionDto, transientResponse: TransientQuestionResponse) => {
    let signature = transientResponse.signatureFile;
    let matchedResponse = applicationResponse?.applicationQuestionResponses?.find(
      (r) => r.applicationQuestionId == question.applicationQuestion?.id
    );
    return (
      <>
        {!isViewMode && !isReviewMode && !matchedResponse && (
          <SignatureBox
            key={`signature:${question.applicationQuestion?.id}`}
            width={350}
            height={200}
            languageId={application.languageId}
            signature={signature}
            onUpdate={(sig) => {
              updateQuestionResponse({ ...transientResponse, signatureFile: sig });
            }}
          />
        )}
        {((isViewMode && matchedResponse) || (isReviewMode && matchedResponse)) && (
          <Stack direction="column" spacing="sm" style={{ maxWidth: "50%", maxHeight: 250 }}>
            <img
              src={matchedResponse.signatureUploadFile?.imagePreviewData}
              style={{ maxWidth: "100%", maxHeight: 250 }}
            />
            <Typography variant="caption">Signed: {formatDateTime(matchedResponse.signatureUploadFile?.createdDate)}</Typography>
          </Stack>
        )}
      </>
    );
  };

  const renderParentSignature = (
    question: PublicApplicationQuestionDto,
    transientResponse: TransientQuestionResponse
  ) => {
    let matchedResponse = applicationResponse?.applicationQuestionResponses?.find(
      (r) => r.applicationQuestionId == question.applicationQuestion?.id
    );
    return (
      <Grid container spacing="sm">
        {!isViewMode && matchedResponse?.signatureUploadFileId == undefined && (
          <Grid item xs={12}>
            <SignatureBox
              key={`signature:${question.applicationQuestion?.id}`}
              width={350}
              height={200}
              languageId={application.languageId}
              signature={transientResponse.signatureFile}
              onUpdate={(sig) => {
                updateQuestionResponse({ ...transientResponse, signatureFile: sig });
              }}
            />
          </Grid>
        )}
        {((isViewMode && matchedResponse) || (isReviewMode && matchedResponse?.signatureUploadFileId)) && (
          <Stack direction="column" spacing="sm" style={{ maxWidth: "100%", maxHeight: 250 }}>
            <img
              src={matchedResponse.signatureUploadFile?.imagePreviewData}
              style={{ maxWidth: "100%", maxHeight: 250 }}
            />
            <Typography variant="caption">Signed: {formatDateTime(matchedResponse.signatureUploadFile?.createdDate)}</Typography>
          </Stack>
        )}
        <Grid item xs={12}>
          <Input
            label="Parent Name"
            sx={{ maxWidth: `100%` }}
            value={transientResponse.writeIn}
            required={true}
            placeholder={application.languageId == 2 ? "Nombre del Padre" : "Parent Name..."}
            disabled={isViewMode}
            onChange={(e) => {
              updateQuestionResponse({
                ...transientResponse,
                writeIn: e.target.value,
              });
            }}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      {matchTemplatePath(homelessDefinitionPageRoute.templatePath) && <Outlet />}
      <Stack px={3} py={3} spacing="lg" width="100%" id="student-application">
        <Grid container id={`topPage`} spacing="sm">
          {application &&
            application.applicationSections
              ?.filter(
                (s) =>
                  s.pageIndex == currentPageIndex &&
                  (maxDisplayOrder == null || s.applicationQuestions?.some((q) => q.displayOrder <= maxDisplayOrder))
              )
              .map((s, index) => renderSection(s, index))}

          {maxDisplayOrder != null && !canIncreasePageIndex() && endAnswerText != null && endAnswerTextField()}

          {!application.optionalSignatures && !canIncreasePageIndex() && (
            <Grid item xs={12}>
              <Grid container spacing="lg">
                {parentSignatureField()}
                {studentSignatureField()}
              </Grid>
            </Grid>
          )}

          <Grid item xs={12}>
            <Stack direction="row" justifyContent={"space-between"} alignItems="center" pt={2}>
              {currentPageIndex != 1 && (
                <Button variant="contained" onClick={() => decreasePageIndex()}>
                  {application.languageId == 2 ? "Anterior" : "Previous"}
                </Button>
              )}
              {currentPageIndex == 1 && <Box />}

              {canIncreasePageIndex() && (
                <Button
                  // disabled={hasUnansweredQuestions()}
                  variant="contained"
                  onClick={() => increasePageIndex()}
                >
                  {application.languageId == 2 ? "Proximo" : "Next"}
                </Button>
              )}

              {!isViewMode && !canIncreasePageIndex() && (
                <Button variant="contained" onClick={() => submitApplication()}>
                  {application.languageId == 2 ? "Enviar" : "Submit"}
                </Button>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </>
  );
};
